"use client";

import React, { useMemo, useState } from 'react';
import BrandLogo from '@/components/ui/BrandLogo';
import styles from './experience-page.module.css';

export type DirectoryBrand = {
  slug: string;
  name: string;
  domain?: string;
  logoUrl?: string;
  years: string;
  projectType?: string;
  role?: string;
  description?: string;
  relationship: 'direct' | 'agency';
};

const filters = [
  { id: 'all', label: 'All brands' },
  { id: 'direct', label: 'In-house & direct' },
  { id: 'agency', label: 'Agency & consulting' },
] as const;

type FilterId = (typeof filters)[number]['id'];

export default function BrandDirectory({ brands }: { brands: DirectoryBrand[] }) {
  const [filter, setFilter] = useState<FilterId>('all');

  const counts = useMemo(
    () => ({
      all: brands.length,
      direct: brands.filter((b) => b.relationship === 'direct').length,
      agency: brands.filter((b) => b.relationship === 'agency').length,
    }),
    [brands]
  );

  const visible = filter === 'all' ? brands : brands.filter((b) => b.relationship === filter);

  return (
    <>
      <div className={styles.filterBar} role="group" aria-label="Filter brands">
        {filters.map((f) => (
          <button
            key={f.id}
            type="button"
            className={`${styles.filterChip} ${filter === f.id ? styles.filterChipActive : ''}`}
            aria-pressed={filter === f.id}
            onClick={() => setFilter(f.id)}
          >
            {f.label}
            <span className={styles.filterCount}>{counts[f.id]}</span>
          </button>
        ))}
      </div>

      <ul className={styles.brandGrid}>
        {visible.map((b) => (
          <li key={b.slug} id={`brand-${b.slug}`} className={styles.brandCard}>
            <div className={styles.brandTop}>
              <div className={styles.brandLogo}>
                <BrandLogo logoUrl={b.logoUrl} domain={b.domain} alt="" width={40} height={40} />
              </div>
              <div className={styles.brandHeading}>
                <h3 className={styles.brandName}>{b.name}</h3>
                <span className={styles.brandYears}>{b.years}</span>
              </div>
              <span className={`${styles.brandBadge} ${b.relationship === 'agency' ? styles.brandBadgeAgency : ''}`}>
                {b.relationship === 'agency' ? 'Agency' : 'Direct'}
              </span>
            </div>
            {b.description && <p className={styles.brandDesc}>{b.description}</p>}
            {(b.projectType || b.role) && (
              <div className={styles.brandMeta}>
                {b.projectType && <span>{b.projectType}</span>}
                {b.role && <span>{b.role}</span>}
              </div>
            )}
          </li>
        ))}
      </ul>
    </>
  );
}
